import AddIcon from "@mui/icons-material/Add";
import {
    Box,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import React from "react";
import { AppAutoCompleteWithSearchAPI } from "../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";

interface Item {
  id: number;
  description: string;
  referenceCode?: string;
  quantity: number | string;
  received: number;
  price: number | string;
  tax: number | string;
  total: number | string;
  isExisting?: boolean; // Add flag to differentiate existing vs new items
}

interface PurchaseOrderItemsTableProps {
  items: Item[];
  onItemChange: (index: number, field: keyof Item, value: any) => void;
  onAddItem: () => void;
  onDeleteItem: (index: number) => void;
  fetchPartOptions: (page: number, search: string) => Promise<any>;
  editable?: boolean;
}

const PurchaseOrderItemsTable: React.FC<PurchaseOrderItemsTableProps> = ({
  items,
  onItemChange,
  onAddItem,
  onDeleteItem,
  fetchPartOptions,
  editable = true,
}) => {
  const toNumber = (value: number | string | undefined): number => {
    const parsed = typeof value === "number" ? value : parseFloat(value || "0");
    return Number.isFinite(parsed) ? parsed : 0;
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="subtitle1"
        sx={{ textAlign: "left", fontWeight: "600", color: "#1E1E1E", mb: 2, fontSize: "1.125rem" }}>
        Items
      </Typography>
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: "50px" }}>#</TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: "250px" }}>Product Description</TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: "130px" }}>Reference Code</TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: "100px" }}>Quantity Received</TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: "100px" }}>Quantity</TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: "120px" }}>Unit Price</TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: "80px" }}>Tax %</TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: "120px" }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index}>
                {/* Column 1: # */}
                <TableCell sx={{ fontSize: "0.875rem" }}>{index + 1}</TableCell>
                
                {/* Column 2: Product Description */}
                <TableCell sx={{ minWidth: "250px" }}>
                  {item.isExisting ? (
                    // For existing items, show description as read-only text
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {item.description}
                    </Typography>
                  ) : (
                    // For new items, show autocomplete
                    <AppAutoCompleteWithSearchAPI
                      key={`part-select-${index}`}
                      onChange={(e: any, value: any) => {
                        console.log("Autocomplete onChange value:", value); // Debug log
                        onItemChange(index, "description", value); // Pass the entire value object, not just the label
                      }}
                      initialPageNumber={0}
                      value={item.description ? { value: item.id?.toString() || "", label: item.description, data: item } : null}
                      getData={({ currentPageNumber, currentSearchQueryTerm }: any) => fetchPartOptions(currentPageNumber, currentSearchQueryTerm)}
                      disabled={!editable}
                    />
                  )}
                </TableCell>
                
                {/* Column 3: Reference Code */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#666" }}>
                    {item.referenceCode || "-"}
                  </Typography>
                </TableCell>
                
                {/* Column 4: Quantity Received */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                    {item.received || 0}
                  </Typography>
                </TableCell>
                
                {/* Column 4: Quantity */}
                <TableCell>
                  {editable ? (
                    <TextField
                      fullWidth
                      value={item.quantity ?? ""}
                      onChange={e => onItemChange(index, "quantity", e.target.value)}
                      variant="standard"
                      disabled={!editable}
                      InputProps={{ style: { fontSize: "0.875rem" } }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {toNumber(item.quantity)}
                    </Typography>
                  )}
                </TableCell>
                
                {/* Column 5: Unit Price */}
                <TableCell>
                  {editable && !item.isExisting ? (
                    <TextField
                      fullWidth
                      value={item.price ?? ""}
                      onChange={e => onItemChange(index, "price", e.target.value)}
                      variant="standard"
                      disabled={!editable}
                      InputProps={{ 
                        style: { fontSize: "0.875rem" },
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>
                      }}
                      placeholder="0.00"
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      ₹ {toNumber(item.price).toFixed(2)}
                    </Typography>
                  )}
                </TableCell>
                
                {/* Column 6: Tax % */}
                <TableCell>
                  {editable ? (
                    <TextField
                      fullWidth
                      value={item.tax ?? ""}
                      onChange={e => onItemChange(index, "tax", e.target.value)}
                      variant="standard"
                      disabled={!editable}
                      InputProps={{ 
                        style: { fontSize: "0.875rem" },
                        endAdornment: <span style={{ fontSize: "0.75rem", color: "#666" }}>%</span>
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {toNumber(item.tax)}
                    </Typography>
                  )}
                </TableCell>
                
                {/* Column 7: Total */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                    ₹ {toNumber(item.total).toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {editable && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <IconButton onClick={onAddItem} sx={{ color: "#1976D2", mr: 1 }} size="small">
            <AddIcon fontSize="small" />
          </IconButton>
          <Typography variant="body1" sx={{ cursor: "pointer", color: "#1976D2", fontSize: "0.875rem" }} onClick={onAddItem}>
            Add Item
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PurchaseOrderItemsTable;
